import { Link } from 'react-router-dom';
import PublicNav from '../components/layout/PublicNav';
import logo from '../assets/logo140.png';

const LAST_UPDATED = 'April 3, 2026';
const CONTACT_EMAIL = 'legal@plannizzi.com';

const sections = [
  { id: 'acceptance', title: '1. Acceptance of Terms' },
  { id: 'service', title: '2. Description of Service' },
  { id: 'disclaimer', title: '3. Not Financial Advice' },
  { id: 'accounts', title: '4. Accounts & Registration' },
  { id: 'subscription', title: '5. Subscription & Billing' },
  { id: 'acceptable-use', title: '6. Acceptable Use' },
  { id: 'intellectual-property', title: '7. Intellectual Property' },
  { id: 'data', title: '8. Your Data' },
  { id: 'termination', title: '9. Termination' },
  { id: 'liability', title: '10. Limitation of Liability' },
  { id: 'changes', title: '11. Changes to Terms' },
  { id: 'governing-law', title: '12. Governing Law' },
  { id: 'contact', title: '13. Contact Us' },
];

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-surface text-primary">
      <PublicNav hideAuth={false} />

      <div className="max-w-6xl mx-auto px-6 py-12 flex gap-12">

        {/* Sticky sidebar TOC — hidden on mobile */}
        <aside className="hidden lg:block w-56 shrink-0">
          <div className="sticky top-24 space-y-1">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Contents</p>
            {sections.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="block text-sm text-slate-500 hover:text-primary transition-colors py-0.5"
              >
                {s.title}
              </a>
            ))}
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0 max-w-3xl">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Terms of Service</h1>
            <p className="text-sm text-slate-500">Last updated: {LAST_UPDATED}</p>
          </div>

          <div className="space-y-1 text-slate-400 text-sm bg-surface-card border border-surface-border rounded-xl px-5 py-4 mb-10">
            <p>These Terms of Service ("Terms") govern your access to and use of <strong className="text-primary">Plannizzi AI</strong> ("Service"), operated by Plannizzi AI ("we", "us", or "our").</p>
            <p className="mt-2">Please read these Terms carefully. By creating an account or using the Service, you agree to be bound by these Terms.</p>
          </div>

          {/* Section 1 */}
          <section id="acceptance" className="mb-10 scroll-mt-24">
            <h2 className="text-xl font-semibold mb-4">1. Acceptance of Terms</h2>
            <p className="text-slate-400 text-sm mb-3">By accessing or using Plannizzi AI, you confirm that you are at least <strong className="text-primary">18 years old</strong>, have the legal capacity to enter into a binding agreement, and agree to comply with these Terms and our <Link to="/privacy" className="text-brand hover:underline">Privacy Policy</Link>.</p>
            <p className="text-slate-400 text-sm">If you are using the Service on behalf of an organization, you represent that you have authority to bind that organization to these Terms.</p>
          </section>

          {/* Section 2 */}
          <section id="service" className="mb-10 scroll-mt-24">
            <h2 className="text-xl font-semibold mb-4">2. Description of Service</h2>
            <p className="text-slate-400 text-sm mb-3">Plannizzi AI is an AI-powered personal financial planning platform. The Service allows you to:</p>
            <ul className="space-y-2 text-slate-400 text-sm list-disc list-inside mb-4">
              <li>Create and track personal financial goals.</li>
              <li>Receive AI-generated monthly savings plans with projected scenarios.</li>
              <li>Monitor your progress against financial milestones.</li>
              <li>Access budgeting and transaction tools (Pro plan).</li>
            </ul>
            <p className="text-slate-400 text-sm">The Service is provided on a <strong className="text-primary">Free</strong> or <strong className="text-primary">Pro</strong> subscription basis. Features available under each plan are described on our <Link to="/pricing" className="text-brand hover:underline">Pricing page</Link> and may change over time.</p>
          </section>

          {/* Section 3 — critical disclaimer */}
          <section id="disclaimer" className="mb-10 scroll-mt-24">
            <h2 className="text-xl font-semibold mb-4">3. Not Financial Advice</h2>
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl px-5 py-4 text-sm text-yellow-200 mb-4">
              <p className="font-semibold mb-1">⚠ Important Disclaimer</p>
              <p>Plannizzi AI is an <strong>educational and informational tool only</strong>. The plans, projections, and scenarios generated by our AI are based solely on the data you provide and general financial modeling. They do <strong>not</strong> constitute professional financial, investment, legal, or tax advice.</p>
            </div>
            <p className="text-slate-400 text-sm mb-3">Plannizzi AI is not a registered investment advisor, broker-dealer, or financial institution. The Service does not manage, hold, or invest your money. AI-generated plans are illustrative projections based on static assumptions and do not account for market volatility, tax implications, personal circumstances, or other factors that a licensed professional would consider.</p>
            <p className="text-slate-400 text-sm">Before making any significant financial decisions, you should consult a qualified financial advisor, tax professional, or attorney. You use the Service and act on its outputs entirely at your own risk.</p>
          </section>

          {/* Section 4 */}
          <section id="accounts" className="mb-10 scroll-mt-24">
            <h2 className="text-xl font-semibold mb-4">4. Accounts & Registration</h2>
            <p className="text-slate-400 text-sm mb-3">You must create an account to use the Service. You agree to:</p>
            <ul className="space-y-2 text-slate-400 text-sm list-disc list-inside mb-4">
              <li>Provide accurate, current, and complete registration information.</li>
              <li>Maintain the security of your password and promptly notify us of any unauthorized access.</li>
              <li>Accept responsibility for all activity that occurs under your account.</li>
              <li>Not share your account credentials or allow others to access your account.</li>
            </ul>
            <p className="text-slate-400 text-sm">We reserve the right to suspend or terminate accounts that contain false information or violate these Terms.</p>
          </section>

          {/* Section 5 */}
          <section id="subscription" className="mb-10 scroll-mt-24">
            <h2 className="text-xl font-semibold mb-4">5. Subscription & Billing</h2>

            <h3 className="font-medium text-primary mb-2">Free Plan</h3>
            <p className="text-slate-400 text-sm mb-4">The Free plan is provided at no cost and includes 1 financial goal and 1 AI-generated plan. Free plan features may be modified or discontinued at any time with reasonable notice.</p>

            <h3 className="font-medium text-primary mb-2">Pro Plan</h3>
            <p className="text-slate-400 text-sm mb-4">Pro subscriptions are billed on a monthly or annual basis via <strong className="text-primary">Stripe</strong>. By subscribing, you authorize us to charge your payment method on a recurring basis until you cancel.</p>

            <h3 className="font-medium text-primary mb-2">Cancellation & Refunds</h3>
            <p className="text-slate-400 text-sm mb-3">You may cancel your Pro subscription at any time from your account settings. Cancellation takes effect at the end of the current billing period — you retain Pro access until then. We do not offer prorated refunds for partial billing periods unless required by applicable law.</p>

            <h3 className="font-medium text-primary mb-2">Price Changes</h3>
            <p className="text-slate-400 text-sm">We may change subscription prices with at least <strong className="text-primary">30 days' notice</strong>. Continued use of the Pro plan after a price change constitutes acceptance of the new pricing.</p>
          </section>

          {/* Section 6 */}
          <section id="acceptable-use" className="mb-10 scroll-mt-24">
            <h2 className="text-xl font-semibold mb-4">6. Acceptable Use</h2>
            <p className="text-slate-400 text-sm mb-3">You agree not to:</p>
            <ul className="space-y-2 text-slate-400 text-sm list-disc list-inside">
              <li>Use the Service for any unlawful purpose or in violation of applicable laws.</li>
              <li>Attempt to reverse-engineer, decompile, or extract source code from the Service.</li>
              <li>Access the Service through automated means (bots, scrapers) without our written permission.</li>
              <li>Attempt to probe, scan, or test the vulnerability of our systems.</li>
              <li>Submit false, misleading, or intentionally extreme inputs to abuse AI generation resources.</li>
              <li>Resell, sublicense, or commercially exploit the Service without authorization.</li>
              <li>Attempt to impersonate another user or Plannizzi AI staff.</li>
            </ul>
            <p className="text-slate-400 text-sm mt-4">We reserve the right to investigate and take appropriate action against violations, including suspension or termination of your account.</p>
          </section>

          {/* Section 7 */}
          <section id="intellectual-property" className="mb-10 scroll-mt-24">
            <h2 className="text-xl font-semibold mb-4">7. Intellectual Property</h2>
            <p className="text-slate-400 text-sm mb-3">The Plannizzi AI name, logo, website design, and software are the exclusive property of Plannizzi AI and protected by applicable intellectual property laws. Nothing in these Terms grants you any right to use our trademarks or branding without prior written consent.</p>
            <p className="text-slate-400 text-sm">The AI-generated financial plans produced for your account are provided for your personal use. You own the underlying data you input; we retain no ownership over your personal financial data.</p>
          </section>

          {/* Section 8 */}
          <section id="data" className="mb-10 scroll-mt-24">
            <h2 className="text-xl font-semibold mb-4">8. Your Data</h2>
            <p className="text-slate-400 text-sm mb-3">You retain ownership of all personal and financial data you submit to the Service. By using the Service, you grant us a limited, non-exclusive, royalty-free license to process your data solely for the purpose of providing the Service to you.</p>
            <p className="text-slate-400 text-sm">Our data practices are governed by our <Link to="/privacy" className="text-brand hover:underline">Privacy Policy</Link>, which is incorporated into these Terms by reference. If you delete your account, your data will be removed in accordance with the Privacy Policy.</p>
          </section>

          {/* Section 9 */}
          <section id="termination" className="mb-10 scroll-mt-24">
            <h2 className="text-xl font-semibold mb-4">9. Termination</h2>
            <p className="text-slate-400 text-sm mb-3">You may delete your account at any time from the account settings page. Upon deletion, your access to the Service ends immediately and your data will be scheduled for removal per our Privacy Policy.</p>
            <p className="text-slate-400 text-sm mb-3">We reserve the right to suspend or permanently terminate your account, with or without notice, if you:</p>
            <ul className="space-y-2 text-slate-400 text-sm list-disc list-inside">
              <li>Violate any provision of these Terms.</li>
              <li>Engage in fraudulent, abusive, or illegal behavior.</li>
              <li>Fail to pay applicable subscription fees.</li>
            </ul>
            <p className="text-slate-400 text-sm mt-4">Sections 3 (Not Financial Advice), 7 (Intellectual Property), 10 (Limitation of Liability), and 12 (Governing Law) survive termination.</p>
          </section>

          {/* Section 10 */}
          <section id="liability" className="mb-10 scroll-mt-24">
            <h2 className="text-xl font-semibold mb-4">10. Limitation of Liability</h2>
            <p className="text-slate-400 text-sm mb-3">To the maximum extent permitted by applicable law, Plannizzi AI and its officers, employees, and affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or goodwill, arising out of or in connection with your use of the Service or reliance on AI-generated plans.</p>
            <p className="text-slate-400 text-sm mb-3">The Service is provided <strong className="text-primary">"as is"</strong> and <strong className="text-primary">"as available"</strong> without warranties of any kind, express or implied, including warranties of merchantability, fitness for a particular purpose, or non-infringement.</p>
            <p className="text-slate-400 text-sm">In jurisdictions that do not allow the exclusion of certain warranties or limitation of liability, our liability is limited to the maximum extent permitted by law. Our aggregate liability to you for any claims arising from the Service shall not exceed the amount you paid us in the three (3) months preceding the claim.</p>
          </section>

          {/* Section 11 */}
          <section id="changes" className="mb-10 scroll-mt-24">
            <h2 className="text-xl font-semibold mb-4">11. Changes to Terms</h2>
            <p className="text-slate-400 text-sm">We may update these Terms from time to time. We will notify you by email and update the "Last updated" date at least <strong className="text-primary">7 days</strong> before material changes take effect. Continued use of the Service after the effective date of updated Terms constitutes your acceptance. If you disagree with the changes, your sole remedy is to stop using the Service and delete your account.</p>
          </section>

          {/* Section 12 */}
          <section id="governing-law" className="mb-10 scroll-mt-24">
            <h2 className="text-xl font-semibold mb-4">12. Governing Law</h2>
            <p className="text-slate-400 text-sm">These Terms are governed by and construed in accordance with applicable laws. Any disputes arising under or in connection with these Terms shall be resolved through good-faith negotiation. If a dispute cannot be resolved informally, you agree to binding arbitration or the courts of the jurisdiction where Plannizzi AI operates, as applicable.</p>
          </section>

          {/* Section 13 */}
          <section id="contact" className="mb-10 scroll-mt-24">
            <h2 className="text-xl font-semibold mb-4">13. Contact Us</h2>
            <p className="text-slate-400 text-sm mb-3">If you have questions about these Terms, contact us at:</p>
            <div className="bg-surface-card border border-surface-border rounded-xl px-5 py-4 text-sm text-slate-400">
              <p className="font-medium text-primary">Plannizzi AI</p>
              <p>Email: <a href={`mailto:${CONTACT_EMAIL}`} className="text-brand hover:underline">{CONTACT_EMAIL}</a></p>
            </div>
          </section>

        </main>
      </div>

      {/* Footer */}
      <footer className="border-t border-surface-border py-8 px-6 mt-4">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-600">
          <div className="flex items-center gap-2">
            <img src={logo} alt="Plannizzi AI" className="h-7 w-auto" />
            <span className="font-medium">Plannizzi AI</span>
          </div>
          <p>© {new Date().getFullYear()} Plannizzi AI. All rights reserved.</p>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-slate-400 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-slate-400 transition-colors">Terms of Service</Link>
            <Link to="/pricing" className="hover:text-slate-400 transition-colors">Pricing</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
